<template>
	<span :id="id" :key="langKey" v-dis.opacity="iDisabled" v-tip="changed ? t('to.common.originalValue', getLabel(valueStore[0])) : ''" :flex="flex ? flex : null" class="to-segment" :class="setClass" :style="setStyle">
		<span v-if="verifiable && (rule || ToFormItem || required)" v-show="false" :rule="rule" :req="validateRequired" :desc="validateDesc" class="to-validate" :value="selected"></span>
		<span v-for="(item, index) in options" :key="index" v-tip="item.tip ? item.tip : ''" v-dis.opacity="item.iD" class="to-segment-item" :class="setItemClass(item)" @click="change(item, index)">
			<slot v-if="type === 'color'">
				<div class="to-segment-color-box" :style="{ 'background-color': item[iValueText] }"></div>
			</slot>
			<template v-else>
				<span class="to-segment-check"></span>
				<span class="to-segment-content">
					<slot :item="item" :index="index">
						<to-icon v-if="item[iIconText]" class="to-segment-icon" :value="item[iIconText]"></to-icon>
						{{ item[iLabelText] }}
					</slot>
				</span>
			</template>
		</span>
		<span v-if="errorTip" v-color="'danger'" class="to-box-tip">
			<template v-if="errorType === 'rule'">
				{{ t('to.segment.selectError') }}
				<to-icon v-tip.danger="errorTip" value="help" color="danger" mode="toast"></to-icon>
			</template>
			<template v-else> {{ t('to.segment.required') }} </template>
		</span>
	</span>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { t, langKey } from '@touchvue/locale'
import { useFormItem } from '@touchvue/hooks'
import { ToIcon } from '@touchvue/components'
import { vDis, vTip, vColor } from '@touchvue/directives'

defineOptions({
	name: 'ToSegment'
})

const props = defineProps({
	flex: Boolean,
	id: {
		type: String,
		default: ''
	},
	col: {
		type: [String, Number],
		default: ''
	},
	data: {
		type: [String, Array],
		default: ''
	},
	type: {
		type: String,
		default: ''
	},
	modelValue: {
		type: [String, Number, Boolean, Array],
		default: ''
	},
	mode: {
		type: String,
		default: 'default'
	},
	itemPadding: {
		type: [String, Number],
		default: 'default'
	},
	color: {
		type: String,
		default: ''
	},
	width: {
		type: [String, Number],
		default: ''
	},
	labelText: {
		type: String,
		default: 'label'
	},
	valueText: {
		type: String,
		default: 'value'
	},
	iconText: {
		type: String,
		default: 'icon'
	},
	disabled: {
		type: Boolean,
		default: false
	},
	clearable: {
		type: Boolean,
		default: false
	},
	multiple: {
		type: Boolean,
		default: false
	},
	inline: {
		type: Boolean,
		default: false
	},
	api: {
		type: String,
		default: ''
	},
	apiParam: {
		type: Object,
		default: () => ({})
	},
	apiLabel: {
		type: String,
		default: ''
	},
	apiHeaders: {
		type: Object,
		default: () => ({})
	},
	resPath: {
		type: String,
		default: ''
	},
	errorPath: {
		type: String,
		default: ''
	},
	successCode: {
		type: [String, Number],
		default: ''
	},
	beforeChange: {
		type: Function,
		default: null
	},
	valueType: {
		type: String,
		default: 'string'
	},
	desc: {
		type: String,
		default: ''
	},
	rule: {
		type: [String, Array, Object],
		default: ''
	},
	required: {
		type: Boolean,
		default: false
	},
	httpRequest: {
		type: Function,
		default: null
	}
})

const emit = defineEmits(['change', 'option-loaded', 'update:modelValue'])

// 表单验证相关 errorTip, errorType,
const { verifiable, validateRequired, validateDesc, ToForm, ToFormItem } = useFormItem(props)

// 响应式数据
const options = ref([])
const selected = ref('')
const label = ref('')
const iDisabled = ref(false)
const changeBySelf = ref(false)
const errorTip = ref('')
const errorType = ref('rule')
// 计算是否发生变化（用于comparable模式）
const changed = computed(() => {
	if ((props.comparable || ToForm?.comparable) && valueStore.value.length > 0 && currentValue.value !== valueStore.value[0]) {
		return true
	} else {
		return false
	}
})
const valueStore = ref([])

// 内部属性
const iLabelText = ref('label')
const iValueText = ref('value')
const iIconText = ref('icon')
const iResPath = ref('')
const iSuccessCode = ref('')
const iApiParam = ref({})
const iApiLabel = ref('')
const iApiHeaders = ref({})
const iErrorPath = ref('')

const setClass = computed(() => {
	const arr = ['to-disable-target']
	if (errorTip.value) {
		arr.push('to-segment-mode-line')
		arr.push('to-segment-color-danger')
		arr.push('to-segment-tip-show')
	} else {
		if (props.type) {
			arr.push(`type-${props.type}`)
		}
		// FIXME: 点击闪红色边框
		// if (changeBySelf.value) {
		// 	arr.push('to-segment-tip-show')
		// }
		if (props.mode) {
			arr.push(`mode-${props.mode}`)
		}
		if (props.multiple) {
			arr.push('is-multiple')
		}
		if (changed.value) {
			arr.push('is-changed')
		}
		if (ToForm?.comparable || props.comparable) {
			arr.push('to-compare')
		}
		if (props.col) {
			arr.push(`col-${props.col}`)
			arr.push(`is-col`)
		}
	}
	return arr.join(' ')
})

const setStyle = computed(() => {
	var obj = {}
	if (props.width) {
		if (!isNaN(props.width)) {
			obj.width = props.width + 'em'
		} else if (props.width === 'full') {
			obj.width = '100%'
		} else {
			obj.width = props.width
		}
	}
	return obj
})

// 计算当前值
const currentValue = computed(() => {
	return props.modelValue ? props.modelValue : props.value
})

// 监听器
watch(
	() => props.disabled,
	val => {
		iDisabled.value = val
	}
)

watch(
	() => currentValue.value,
	val => {
		if (!changeBySelf.value) {
			if (Array.isArray(val)) {
				selected.value = val.join(',')
			} else {
				selected.value = val
			}
			if (props.valueType === 'array') {
				emit('change', selected.value ? selected.value.split(',') : [], getIndex(val))
			} else {
				emit('change', selected.value, getIndex(val))
			}
			setLabel()
			if (!valueStore.value.length) {
				valueStore.value = [val]
			}
		}
	}
)

watch(
	() => props.data,
	() => {
		setOptions()
	},
	{ deep: true }
)

watch(
	() => props.api,
	() => {
		setOptions()
	}
)

// 生命周期
onMounted(() => {
	selected.value = currentValue.value
	valueStore.value = [currentValue.value]
	iLabelText.value = props.labelText
	iValueText.value = props.valueText
	iIconText.value = props.iconText
	iResPath.value = props.resPath
	iSuccessCode.value = props.successCode
	iApiParam.value = props.apiParam
	iApiLabel.value = props.apiLabel
	iApiHeaders.value = props.apiHeaders
	iErrorPath.value = props.errorPath
	iDisabled.value = props.disabled
	setOptions()
})

// 方法
function setOptions() {
	// 同步选项
	if (props.data) {
		if (typeof props.data === 'string') {
			let arr = []
			for (let i = 0; i < props.data.split(',').length; i++) {
				const el = props.data.split(',')[i]
				let obj = {}
				obj[iLabelText.value] = obj[iValueText.value] = el
				arr.push(obj)
			}
			options.value = arr
		} else {
			options.value = [...props.data]
		}
		setLabel()
	}
	// 异步选项
	if (props.api) {
		if (props.httpRequest) {
			const requestConfig = {
				url: props.api,
				method: 'GET',
				params: iApiParam.value,
				headers: iApiHeaders.value
			}
			props
				.httpRequest(requestConfig)
				.then(res => {
					let data = res
					if (iResPath.value) {
						const paths = iResPath.value.split('.')
						for (const path of paths) {
							data = data[path]
						}
					}
					if (Array.isArray(data)) {
						options.value = data
						setLabel()
						emit('option-loaded', data)
					} else {
						console.error('segment api返回数据格式错误')
					}
				})
				.catch(err => {
					console.error('segment api请求失败:', err)
				})
		} else {
			console.warn('segment组件需要httpRequest方法来处理api请求')
		}
	}
}

function setLabel() {
	if (!props.multiple) {
		let obj = (() => {
			for (let i = 0; i < options.value.length; i++) {
				const el = options.value[i]
				if (el[iValueText.value] === selected.value) {
					return el
				}
			}
		})()
		if (obj && obj[iLabelText.value]) {
			label.value = obj[iLabelText.value]
		} else {
			label.value = ''
		}
	} else {
		let obj = (() => {
			if (selected.value === '' || selected.value === null || selected.value === undefined) {
				return []
			} else {
				return String(selected.value).split(',')
			}
		})()
		label.value = (() => {
			if (selected.value === '' || selected.value === null || selected.value === undefined) {
				return ''
			} else {
				let arr = []
				options.value.map(option => {
					if (obj.includes(String(option[iValueText.value]))) {
						arr.push(option[iLabelText.value])
					}
				})
				return arr.join(',')
			}
		})()
	}
}

function getLabel(val) {
	let obj = options.value.find(item => item[iValueText.value] === val)
	if (obj && obj[iLabelText.value]) {
		return obj[iLabelText.value]
	}
	return ''
}

function getIndex() {
	return options.value.findIndex(option => option[iValueText.value] === selected.value)
}

function change(item, index) {
	if (item.disabled) return
	if (iDisabled.value) return

	if (props.beforeChange) {
		const fn = props.beforeChange(item, index)
		if (fn && fn.then) {
			fn.then(() => {
				changeAction(item, index)
			}).catch(() => {
				// beforeChange rejected
			})
		} else {
			if (fn !== false) {
				changeAction(item, index)
			}
		}
	} else {
		changeAction(item, index)
	}
}

function changeAction(item, index) {
	errorTip.value = ''
	if (props.multiple) {
		let iValue = (() => {
			if (selected.value === '' || selected.value === null || selected.value === undefined || (Array.isArray(selected.value) && selected.value.length === 0)) {
				return []
			} else {
				return String(selected.value).split(',')
			}
		})()
		if (iValue.includes(String(item[iValueText.value]))) {
			iValue = iValue.filter(el => {
				return el !== String(item[iValueText.value])
			})
		} else {
			iValue.push(String(item[iValueText.value]))
		}
		selected.value = iValue.length ? iValue.join(',') : ''
		label.value = (() => {
			if (!iValue.length) {
				return ''
			} else {
				let arr = []
				options.value.map(option => {
					if (iValue.includes(String(option[iValueText.value]))) {
						arr.push(option[iLabelText.value])
					}
				})
				return arr.join(',')
			}
		})()
		changeBySelf.value = true
		const emitValue = props.valueType === 'array' ? (selected.value ? selected.value.split(',') : []) : selected.value
		emit('update:modelValue', emitValue)
		emit('change', emitValue, index)
		setTimeout(() => {
			changeBySelf.value = false
		}, 50)
	} else {
		if (item[iValueText.value] === selected.value && props.clearable) {
			selected.value = ''
			label.value = ''
			changeBySelf.value = true
			emit('update:modelValue', '')
			emit('change', '', index)
			setTimeout(() => {
				changeBySelf.value = false
			}, 50)
		} else {
			selected.value = item[iValueText.value]
			label.value = item[iLabelText.value]
			changeBySelf.value = true
			emit('update:modelValue', item[iValueText.value])
			emit('change', item[iValueText.value], index)
			setTimeout(() => {
				changeBySelf.value = false
			}, 50)
		}
	}
}

function setItemClass(item) {
	if (props.multiple) {
		if (String(selected.value).split(',').includes(String(item[iValueText.value]))) {
			return 'is-on'
		} else {
			return ''
		}
	} else {
		if (selected.value === item[iValueText.value]) {
			return 'is-on'
		} else {
			return ''
		}
	}
}

function setDisabled(f) {
	iDisabled.value = f
}

function tip(msg) {
	// tip方法实现
	console.log('tip:', msg)
}

// 记录历史值，以便对比或者取值
function compare() {
	valueStore.value.unshift(currentValue.value)
}

// 撤销
function restore() {
	if (valueStore.value.length > 0) {
		const restoredValue = valueStore.value[0]
		valueStore.value.splice(0, 1)
		emit('update:modelValue', restoredValue)
		emit('change', restoredValue)
	}
}

// 暴露方法给父组件
defineExpose({
	setDisabled,
	tip,
	getLabel,
	compare,
	restore,
	changed,
	errorTip,
	errorType
})
</script>
